/**
 * ValidationDashboard Component
 *
 * Main validation UI that combines all validation components.
 * Groups issues by severity, tracks acknowledgments, and controls C10 generation.
 */

import { useState, useMemo, useCallback } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { ValidationSummaryHeader } from './ValidationSummaryHeader';
import { ValidationIssueCard } from './ValidationIssueCard';
import type { SubmissionValidationResult } from '@/types/validation';
import { useAcknowledgedWarnings } from '@/hooks/useValidation';

// =============================================================================
// TYPES
// =============================================================================

export interface ValidationDashboardProps {
  /** Validation result to display */
  result: SubmissionValidationResult | null;
  /** Whether validation is currently in progress */
  isValidating?: boolean;
  /** Callback when revalidate button is clicked */
  onRevalidate?: () => void;
  /** Callback when generate C10 is clicked */
  onGenerate?: () => void;
  /** Whether C10 generation is in progress */
  isGenerating?: boolean;
  /** Callback when apply suggestion is clicked */
  onApplySuggestion?: (
    employeeNi: string,
    field: string,
    value: string
  ) => void;
  /** Callback when edit employee is clicked */
  onEditEmployee?: (employeeNi: string) => void;
  /** Callback when remove employee is clicked */
  onRemoveEmployee?: (employeeNi: string) => void;
}

// =============================================================================
// COLLAPSIBLE SECTION
// =============================================================================

interface CollapsibleSectionProps {
  title: string;
  count: number;
  icon: React.ReactNode;
  bgColor: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function CollapsibleSection({
  title,
  count,
  icon,
  bgColor,
  isOpen,
  onToggle,
  children,
}: CollapsibleSectionProps) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full px-4 py-3 flex items-center justify-between ${bgColor} hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-600" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
          )}
          <span className="flex items-center gap-2">
            {icon}
            <span className="font-medium text-gray-900">{title}</span>
          </span>
        </div>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white text-gray-700 shadow-sm">
          {count}
        </span>
      </button>

      {isOpen && <div className="p-4 space-y-3 bg-white">{children}</div>}
    </div>
  );
}

// =============================================================================
// VALIDATION DASHBOARD
// =============================================================================

export function ValidationDashboard({
  result,
  isValidating = false,
  onRevalidate,
  onGenerate,
  isGenerating = false,
  onApplySuggestion,
  onEditEmployee,
  onRemoveEmployee,
}: ValidationDashboardProps) {
  // Section collapse state
  const [showErrors, setShowErrors] = useState(true);
  const [showWarnings, setShowWarnings] = useState(true);

  // Warning acknowledgment tracking
  const {
    isAcknowledged,
    acknowledge,
    acknowledgeAllForEmployee,
    acknowledgeAll,
    allAcknowledged,
    unacknowledgedCount,
  } = useAcknowledgedWarnings(result);

  // Memoize filtered employees
  const employeesWithErrors = useMemo(() => {
    if (!result) return [];
    return result.employee_results.filter(e => !e.is_valid);
  }, [result]);

  const employeesWithWarnings = useMemo(() => {
    if (!result) return [];
    return result.employee_results.filter(e => e.is_valid && e.has_warnings);
  }, [result]);

  // Get acknowledged codes for an employee
  const getAcknowledgedCodes = useCallback(
    (employeeNi: string): Set<string> => {
      if (!result) return new Set();

      const employeeResult = result.employee_results.find(
        e => e.employee_ni === employeeNi
      );
      if (!employeeResult) return new Set();

      const codes = new Set<string>();
      employeeResult.issues.forEach(issue => {
        if (
          issue.severity === 'WARNING' &&
          isAcknowledged(employeeNi, issue.code)
        ) {
          codes.add(issue.code);
        }
      });
      return codes;
    },
    [result, isAcknowledged]
  );

  // Can generate: no errors AND (no warnings OR all warnings acknowledged)
  const canGenerate = useMemo(() => {
    if (!result) return false;
    if (!result.can_generate) return false;
    if (!result.requires_acknowledgment) return true;
    return allAcknowledged();
  }, [result, allAcknowledged]);

  // Empty state
  if (!result && !isValidating) {
    return (
      <div className="rounded-lg border border-gray-200 p-6 bg-gray-50">
        <div className="text-center">
          <ExclamationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No Validation Results
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Run validation to check for issues before generating the C10 file.
          </p>
          {onRevalidate && (
            <button
              type="button"
              onClick={onRevalidate}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Run Validation
            </button>
          )}
        </div>
      </div>
    );
  }

  // All valid state
  const isAllValid =
    result &&
    !isValidating &&
    result.is_valid &&
    !result.requires_acknowledgment;

  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <ValidationSummaryHeader
        result={result}
        isValidating={isValidating}
        onRevalidate={onRevalidate}
        unacknowledgedWarnings={unacknowledgedCount()}
      />

      {/* All Valid Message */}
      {isAllValid && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-6">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-green-900">
                All Employees Valid
              </h3>
              <p className="mt-1 text-sm text-green-700">
                All {result.summary.total_employees} employees passed
                validation. Ready to generate C10 file.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Errors Section */}
      {employeesWithErrors.length > 0 && (
        <CollapsibleSection
          title="Errors - Must Fix Before Generating"
          count={result?.summary.total_errors ?? 0}
          icon={<ExclamationCircleIcon className="h-5 w-5 text-red-500" />}
          bgColor="bg-red-50"
          isOpen={showErrors}
          onToggle={() => setShowErrors(!showErrors)}
        >
          {employeesWithErrors.map(employee => (
            <ValidationIssueCard
              key={employee.employee_ni}
              employeeResult={employee}
              acknowledgedCodes={getAcknowledgedCodes(employee.employee_ni)}
              showOnly="errors"
              onApplySuggestion={onApplySuggestion}
              onEditEmployee={onEditEmployee}
              onRemoveEmployee={onRemoveEmployee}
            />
          ))}
        </CollapsibleSection>
      )}

      {/* Warnings Section */}
      {employeesWithWarnings.length > 0 && (
        <CollapsibleSection
          title="Warnings - Review Recommended"
          count={result?.summary.total_warnings ?? 0}
          icon={<ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />}
          bgColor="bg-amber-50"
          isOpen={showWarnings}
          onToggle={() => setShowWarnings(!showWarnings)}
        >
          {/* Acknowledge all button */}
          {unacknowledgedCount() > 0 && (
            <div className="mb-3 pb-3 border-b border-gray-200">
              <button
                type="button"
                onClick={acknowledgeAll}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-100 rounded-md hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                Acknowledge All Warnings ({unacknowledgedCount()})
              </button>
            </div>
          )}

          {employeesWithWarnings.map(employee => (
            <ValidationIssueCard
              key={employee.employee_ni}
              employeeResult={employee}
              acknowledgedCodes={getAcknowledgedCodes(employee.employee_ni)}
              showOnly="warnings"
              onAcknowledgeWarning={acknowledge}
              onAcknowledgeAll={acknowledgeAllForEmployee}
              onApplySuggestion={onApplySuggestion}
              onEditEmployee={onEditEmployee}
              onRemoveEmployee={onRemoveEmployee}
            />
          ))}
        </CollapsibleSection>
      )}

      {/* Generate Button */}
      {onGenerate && result && !isValidating && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">
                Generate C10 File
              </h4>
              {!canGenerate && result.summary.total_errors > 0 && (
                <p className="text-xs text-red-600 mt-1">
                  {result.summary.total_errors} error(s) must be fixed first
                </p>
              )}
              {!canGenerate &&
                result.summary.total_errors === 0 &&
                unacknowledgedCount() > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {unacknowledgedCount()} warning(s) must be acknowledged
                  </p>
                )}
            </div>
            <button
              type="button"
              onClick={onGenerate}
              disabled={!canGenerate || isGenerating}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                canGenerate
                  ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {isGenerating ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                'Generate C10 File'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ValidationDashboard;
